import { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import useNotification from "../../../hooks/notification.hook";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ModalContainer from "../ModalContainer";
import "./EndSessionPopup.scss";
import usePlanCompile from "../../../hooks/plan-compile.hook";
import {
	createAgenda,
	createAnnotation,
	getAnnotation,
	updateAnnotation,
} from "../../../services/agendas.service";
import { usePlanCompileStore } from "../../../store/plan-compile.store";
import { ANNOTATION_TYPE_HELPER, ANNOTATION_TYPE_HELPER_ARRAY, cn } from "../../../lib/utils";
import { formatDate } from "../../../helper/date-formatter.helper";
import { checkScheduledEventAnnotation } from "../../../services/supporto/schedule-events.services";
import dayjs from "dayjs";

export default function EndSessionPopup({
	open,
	setOpen,
	agenda,
	onCancel = () => {},
	annotations,
	loadingAnnotations,
	scheduledEvents,
	organizationId,
	role,
}) {
	const { t } = useTranslation();

	const isScheduledEventEnabled = useMemo(() => ["admin"].includes(role), [role]);

	const [planDetail, updateState] = usePlanCompileStore((state) => [
		state.planDetail,
		state.updateState,
	]);

	const { getStoredChangesBlocks, saveActivities } = usePlanCompile();

	// Set message
	yup.setLocale({
		// use constant translation keys for messages without values
		mixed: {
			required: ({ path }) => t("common:errors.required", { field: t(`common:fields.${path}`) }),
		},
		number: {
			moreThan: ({ path, more }) =>
				t("common:errors.more_than", {
					field: t(`common:fields.${path}`),
					value: more,
				}),
			lessThan: ({ path, less }) =>
				t("common:errors.less_than", {
					field: t(`common:fields.${path}`),
					value: less,
				}),
		},
	});

	// Schema Yup
	const schema = yup.object().shape({
		name_agenda: yup.string().when({
			is: () => agenda === null,
			// biome-ignore lint/suspicious/noThenProperty: Yup's Schema.when API requires the then property.
			then: (schema) => schema.required(),
		}),
		annotation_id: yup.string(),
		type: yup.string().when("annotation_id", {
			is: (val) => val === "",
			// biome-ignore lint/suspicious/noThenProperty: Yup's Schema.when API requires the then property.
			then: (schema) => schema.required(),
		}),
		date_creation: yup.string().required(),
		name_annotation: yup.string().when("annotation_id", {
			is: (val) => val === "",
			// biome-ignore lint/suspicious/noThenProperty: Yup's Schema.when API requires the then property.
			then: (schema) => schema.required(),
		}),
		scheduledEvent: yup.string().optional(),
	});

	const {
		register,
		setValue,
		clearErrors,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema) });

	const { openNotification, helperErrorNotification } = useNotification();

	const [loading, setLoading] = useState(false);

	const [isAlertVisible, setIsAlertVisible] = useState(false);

	const watchAnnotation = watch("annotation_id");

	const navigate = useNavigate();

	/**
	 * Form Handler
	 * @param {form modal} data
	 */
	async function formHandler(data) {
		if (!loading) {
			setLoading(true);

			let idAgenda = agenda ? agenda.id : null;

			if (!agenda) {
				// Create agenda API
				const res = await createAgenda({
					idCorp: planDetail.organization_id,
					idPlan: planDetail.id,
					name: data.name_agenda,
				});

				idAgenda = res.data.id;
			}

			const body = {
				idCorp: planDetail.organization_id,
				...(isScheduledEventEnabled && {
					scheduled_event_id: data.scheduledEvent || null,
				}),
			};

			/* updateAnnotation */
			if (data.annotation_id === "") {
				const blocks = await getStoredChangesBlocks(data.name_annotation);

				// Create annotation API
				createAnnotation(
					{
						...body,
						idAgenda,
						type: data.type,
						name: data.name_annotation,
						date: data.date_creation,
						editorjs: { blocks: blocks },
					},
					(res) => {
						successCallback();
					},
					(err) => {
						setLoading(false);
						helperErrorNotification(err);
					}
				);
			} else {
				// Get annotation Api
				getAnnotation(
					{
						params: {
							id: data.annotation_id,
							idCorp: planDetail.organization_id,
						},
					},
					async (res) => {
						const currentEditor = res.data.editorjs;
						const blocks = await getStoredChangesBlocks(res.data.name);

						currentEditor.blocks.push(
							{
								type: "delimiter",
								data: {},
							},
							...blocks
						);

						// Update annotation Api
						updateAnnotation(
							{
								...body,
								id: res.data.id,
								editorjs: currentEditor,
								name: res.data.name,
								date: res.data.date,
							},
							() => {
								successCallback();
							},
							(err) => {
								setLoading(false);

								helperErrorNotification(err);
							}
						);
					},
					(err) => {
						setLoading(false);

						helperErrorNotification(err);
					}
				);
			}
		}
	}

	/**
	 * Success callback
	 */
	const successCallback = () => {
		setLoading(false);

		saveActivities(planDetail);

		updateState({
			changesList: [],
			isPlanApproved: false,
			trackChanges: {
				plansAttachments: {
					uploaded: [],
					removed: [],
				},
			},
		});

		setOpen(false);

		openNotification({
			text: "Annotazione creata con successo!",
			status: "success",
		});

		setTimeout(() => {
			if (role === "admin") {
				navigate(`/admin/entities/entity/${planDetail.organization_id}`);
			}
			if (role === "reseller") {
				navigate(`/reseller/${planDetail.organization_id}`);
			}
			if (role === "organization") {
				navigate(`/organization`);
			}
		}, 0);
	};

	/**
	 * Errors Form
	 */
	useEffect(() => {
		if (Object.keys(errors).length) {
			const fields = Object.values(errors).map((er) => er.message);
			openNotification({ fields, status: "error" });
		}
	}, [errors]);

	/**
	 * Init value
	 */
	useEffect(() => {
		if (open) {
			clearErrors();
			setValue("name_agenda", "Appuntamenti riservati");
			setValue("name_annotation", "");
			setValue("type", "");
			setValue("date_creation", "");
			setValue("annotation_id", "");
			initScheduledEventHandler();
			setIsAlertVisible(false);
		}
	}, [open]);

	const initTitleHandler = (type, value) => {
		if (!watch("name_annotation") && value) {
			if (type === "date" && watch("type")) {
				const typeAnnotation = watch("type");
				setValue(
					"name_annotation",
					`${ANNOTATION_TYPE_HELPER?.[typeAnnotation]?.label || typeAnnotation} del ${formatDate(value, "DD/MM/YYYY")} - ore ${formatDate(new Date(), "HH:mm")}`
				);
			}

			if (type === "type" && watch("date_creation")) {
				const date = watch("date_creation");
				setValue(
					"name_annotation",
					`${ANNOTATION_TYPE_HELPER?.[value]?.label || value} del ${formatDate(date, "DD/MM/YYYY")} - ore ${formatDate(new Date(), "HH:mm")}`
				);
			}
		}
	};

	const checkScheduledEventSignal = useRef(new AbortController());

	const checkScheduledEventUsed = async (value) => {
		if (!value) {
			setIsAlertVisible(false);
			return;
		}
		checkScheduledEventSignal.current.abort();
		checkScheduledEventSignal.current = new AbortController();
		try {
			const res = await checkScheduledEventAnnotation({
				params: {
					organizationId,
					scheduleEventId: value,
				},
				config: {
					signal: checkScheduledEventSignal.current.signal,
				},
			});
			setIsAlertVisible(res.length > 0);
		} catch (err) {}
	};

	const onAnnotationChange = (annotationId) => {
		if (!annotationId) return;
		const annotation = annotations.find((item) => item.id === Number(annotationId));
		if (!annotation) return;
		setValue("scheduledEvent", annotation?.scheduled_event_id || "");
		setIsAlertVisible(false);
	};

	const initScheduledEventHandler = () => {
		if (!isScheduledEventEnabled) return;
		const now = dayjs();
		const currentEvent =
			scheduledEvents
				?.filter((a) => {
					const start = dayjs.utc(a.calendly_start_time).local();
					return start.isSame(now, "day") && start.isBefore(now);
				})
				?.sort((a, b) =>
					dayjs.utc(b.calendly_start_time).diff(dayjs.utc(a.calendly_start_time))
				)[0] ?? null;

		setValue("scheduledEvent", currentEvent?.id || "");
	};

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen}>
			<div className={"main_modal_content session_popup !max-w-[450px]"}>
				<button
					className="main_close"
					onClick={() => setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				<div className="main_header_modal left_text">
					<span className="title">Termina sessione</span>
				</div>

				{/* Modal Form */}
				<div className={"main_body_modal"}>
					<div className="main_message_box box_info">
						<div className="main_icon">
							<span className="icon_16 default info"></span>
						</div>
						<div className="message">Salva le annotazioni della sessione nel diario.</div>
					</div>

					{agenda && (
						<div className="main_sub_intro pb-4">
							<div className="wrap_sub_intro">
								<div className="main_icon">
									<span className="icon_16 default book"></span>
								</div>
								<div className="subintro">Diario</div>
							</div>

							<div className="main_description">{agenda.name}</div>
						</div>
					)}

					<form
						id="crud-end-session-popup"
						className="global_form_landing"
						onSubmit={handleSubmit(formHandler)}
						noValidate
					>
						{/* Name Agenda */}
						{agenda === null && (
							<div className="base">
								<div className="wrap_input_landing">
									<label htmlFor="session-end-name-diary" className="label_input">
										Titolo diario
									</label>
									<input
										className={`landing big${errors.name_agenda ? " err" : ""}`}
										id="session-end-name-diary"
										type="text"
										{...register("name_agenda")}
									/>
								</div>
							</div>
						)}

						{/* Select annotation */}
						<div className="base">
							<div className={cn("main_select_landing", loadingAnnotations && "dis")}>
								<label htmlFor="crud-agenda-entity" className="label_input">
									Annotazione
								</label>
								<div className="relative">
									<select
										disabled={loadingAnnotations}
										className={"landing big"}
										id="crud-agenda-entity"
										{...register("annotation_id", {
											onChange: (e) => onAnnotationChange(e.target.value),
										})}
									>
										<option value="">+ Nuova annotazione</option>
										{annotations.map((annotation, i) => (
											<option key={i} value={annotation.id}>
												{annotation.name}
											</option>
										))}
									</select>
									<div className="wrap_arrow">
										<span
											className={cn(
												"icon_16 default",
												loadingAnnotations ? "loader" : "chevron icon_mask !bg-interactive-100"
											)}
										></span>
									</div>
								</div>
							</div>
						</div>

						{/* Type */}
						{watchAnnotation === "" && (
							<div className="base">
								<div className="main_select_landing">
									<div className="relative">
										<label htmlFor="session-end-annotation-type" className="label_input">
											Tipologia
										</label>
										<div className="relative">
											<select
												id="session-end-annotation-type"
												className={cn("landing big", errors.type && "err")}
												{...register("type", {
													onChange: (e) => initTitleHandler("type", e.target.value),
												})}
											>
												<option disabled value="">
													Seleziona tipologia
												</option>
												{ANNOTATION_TYPE_HELPER_ARRAY.map((type) => (
													<option key={type.value} value={type.value}>
														{type.label}
													</option>
												))}
											</select>
											<div className="wrap_arrow">
												<span className="icon_16 default chevron icon_mask !bg-interactive-100"></span>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Date */}
						<div className="base">
							<div className="wrap_input_landing date">
								<label htmlFor="session-end-date" className="label_input">
									Data inizio
								</label>
								<div className="relative">
									<input
										{...register("date_creation", {
											onChange: (e) => initTitleHandler("date", e.target.value),
										})}
										className={cn("landing big", errors.date_creation && "err")}
										id="session-end-date"
										type="date"
									/>
									<span className="icon_24 default calendar icon_mask !bg-interactive-100"></span>
								</div>
							</div>
						</div>

						{/* Name annotation */}
						{watchAnnotation === "" && (
							<div className="base fade_in">
								<div className="wrap_input_landing">
									<label htmlFor="session-end-name-annotation" className="label_input">
										Titolo annotazione
									</label>
									<input
										className={cn("landing big", errors.name_annotation && "err")}
										id="session-end-name-annotation"
										type="text"
										{...register("name_annotation")}
									/>
								</div>
							</div>
						)}

						{/* Scheduled Event */}
						{isScheduledEventEnabled && (
							<div className="base">
								<div className="main_select_landing">
									<div className="relative">
										<label htmlFor="session-end-annotation-scheduled-event" className="label_input">
											Appuntamento
										</label>
										<div className="relative">
											<select
												id="session-end-annotation-scheduled-event"
												className={cn("landing big")}
												{...register("scheduledEvent", {
													onChange: (e) => checkScheduledEventUsed(e.target.value),
												})}
											>
												<option value="">Nessun appuntamento</option>
												{scheduledEvents?.map((event) => (
													<option key={event.id} value={event.id}>
														{event.calendly_name} - del{" "}
														{formatDate(event.calendly_start_time, "DD/MM/YYYY HH:mm")}
													</option>
												))}
											</select>
											<div className="wrap_arrow">
												<span className="icon_16 default chevron icon_mask !bg-interactive-100"></span>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{isScheduledEventEnabled && isAlertVisible && (
							<div className="main_message_box box_warning fade_in">
								<div className="main_icon">
									<span className="icon_16 default danger"></span>
								</div>
								<div className="message">
									Questo appuntamento è già associato a un’altra annotazione.
								</div>
							</div>
						)}
					</form>
				</div>

				<div className="main_footer_modal">
					{/* Cancel */}
					<button type="button" onClick={onCancel} className="button line">
						Non salvare
					</button>

					{/* Main Cta */}
					<button type="submit" form="crud-end-session-popup" className="button fill success">
						Salva
					</button>
				</div>
			</div>
		</ModalContainer>
	);
}
